import { Injectable } from '@angular/core';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { BasicDialogService } from '@principle-theorem/ng-shared';
import {
  SchedulingScenario,
  type IReplacedScheduleOccurrence,
} from '@principle-theorem/principle-core';
import { AppointmentPermissions } from '@principle-theorem/principle-core/features';
import {
  type IEventable,
  type ITreatmentCategory,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  filterUndefined,
  snapshot,
} from '@principle-theorem/shared';

@Injectable()
export class SchedulingScenarioService {
  constructor(
    private _organisation: OrganisationService,
    private _basicDialog: BasicDialogService
  ) {}

  async isBlockedByDoubleBooking(
    currentEventable: IEventable,
    treatmentCategory?: DocumentReference<ITreatmentCategory>,
    replacedOccurrence?: IReplacedScheduleOccurrence
  ): Promise<boolean> {
    const canDoubleBook = await snapshot(
      this._organisation.userHasPermission$(
        AppointmentPermissions.SchedulingDoubleBook
      )
    );

    const doubleBookingResults =
      await SchedulingScenario.getByDoubleBookingResults(
        await snapshot(this._organisation.brand$.pipe(filterUndefined())),
        currentEventable,
        treatmentCategory,
        replacedOccurrence
      );

    if (doubleBookingResults.conflictingPreBlocks.length) {
      if (!canDoubleBook) {
        await this._basicDialog.alert({
          title: `Pre-block Conflict`,
          prompt: [
            `The event doesn't match the required treatment category for the pre-block. The following are allowed:`,
            ...doubleBookingResults.allowedTreatmentCategories.map(
              (category) => `- ${category}`
            ),
          ],
          submitLabel: 'Close',
        });
        return true;
      }

      const approveBook = await this._basicDialog.confirm({
        prompt: [
          `Are you sure you want to book over this pre-block? The event doesn't match the required treatment category for the pre-block. The following are allowed:`,
          ...doubleBookingResults.allowedTreatmentCategories.map(
            (category) => `- ${category}`
          ),
        ],
        title: `Pre-block Conflict`,
        submitLabel: 'Yes, book over pre-block',
        submitColor: 'warn',
        cancelLabel: 'Cancel',
      });

      if (!approveBook) {
        return true;
      }
    }

    if (doubleBookingResults.overlapsBlockingEvent) {
      if (!canDoubleBook) {
        await this._basicDialog.alert({
          title: `No permission to double-book`,
          prompt: `You don't have permission to double-book appointments. Please change the appointment time to continue.`,
          submitLabel: 'Close',
        });
        return true;
      }

      const approveBook = await this._basicDialog.confirm({
        prompt: `Are you sure you want to double-book this appointment? There's currently a blocking event that overlaps this time.`,
        title: 'Are you sure you want to double-book?',
        submitLabel: 'Yes, double-book',
        submitColor: 'warn',
        cancelLabel: 'Cancel',
      });

      if (!approveBook) {
        return true;
      }
    }

    return false;
  }
}
